<template>
  <div class="quiz-container" :class="{'dark-theme': isDarkTheme}">
    <h3 class="disclaimer">This quiz is generated by AI and may not be fully accurate; please verify content with course materials.</h3>
    <v-card v-for="(question, questionIndex) in quizData">
      <v-card-title v-dompurify-html="formatText(question.question)"></v-card-title>
      <v-radio-group v-model="answers[questionIndex]" :disabled="isSubmitted" >
        <div v-for="(answerValue, answerKey) in question.answers">
          <v-radio :value="answerKey">
            <template v-slot:label>
              <div class="quiz-answer" :class="{correct: isSubmitted &&  isCorrect(questionIndex, answerKey), incorrect:isSubmitted && isIncorrect(questionIndex, answerKey)}" v-dompurify-html="formatText(answerValue)"></div>
              <div v-if="isSubmitted &&  isCorrect(questionIndex, answerKey)"><v-icon  icon="mdi-arrow-right"></v-icon> Correct</div>
              <div v-if="isSubmitted &&  isIncorrect(questionIndex, answerKey)"><v-icon  icon="mdi-arrow-right"></v-icon> Incorrect</div>
            </template>
          </v-radio>
          <div class="quiz-justification" v-if="isSubmitted  && isCorrect(questionIndex, answerKey)">
            <span v-dompurify-html="formatText(question.rationale)"></span>
            <ol>
              <div v-if="prepareQuizSourcesForDisplay(question).length > 0">
                Sources:
                <li id="quizSourcesContainer" v-for="source in prepareQuizSourcesForDisplay(question)">
                  <span v-dompurify-html="source.source"></span>
                  <font-awesome-icon v-if="source.isVideo" class="video-icon" :icon="['fas', 'video']" />
                  <font-awesome-icon v-if="source.isLink" class="doc-icon" :icon="['fas', 'arrow-up-right-from-square']" />
                </li>
              </div>
              <div v-else>
                No sources returned.
              </div>
            </ol>
          </div>
        </div>
      </v-radio-group>
    </v-card>
    <div class="d-flex justify-content-between align-items-center mt-2">
    <h2 class="mb-0" >Quiz - Score {{totalCorrect}} / {{totalQuestions}}</h2>
      <div class="flex-grow-1"></div>
      <div
        class="button-tooltip-container"
        :class="{ 'show-tooltip': !isSubmitted && !allQuestionsAnswered }"
        style="display: inline-block; position: relative;"
      >
        <v-btn
          :disabled="!isSubmitted && !allQuestionsAnswered"
          @click="isSubmitted ? confirmReset() : submitQuiz()"
          :color="buttonColor"
        >
          {{ isSubmitted ? 'Reset Quiz' : 'Submit Quiz' }}
        </v-btn>
        <div
          v-if="!isSubmitted && !allQuestionsAnswered"
          class="custom-tooltip"
        >
          Answer all questions to enable the Submit Quiz button
        </div>
      </div>
    </div>

    <v-dialog v-model="showResetConfirmation" max-width="400">
      <v-card>
        <v-card-title class="text-h5 pa-4">
          Confirm Reset
        </v-card-title>
        <v-card-text class="pa-4">
          Are you sure you want to reset the quiz? This will clear all your answers.
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-4"
            variant="text"
            @click="showResetConfirmation = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#e21833"
            variant="elevated"
            @click="confirmAndReset"
          >
            Reset Quiz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>
<script>
import {computed, inject} from "vue";
import { useTheme } from 'vuetify'
import showdown from 'showdown';
import showdownHighlight from 'showdown-highlight';
import katex from 'katex';
import splitAtDelimiters from "katex/contrib/auto-render/splitAtDelimiters";
import 'katex/dist/katex.min.css';

const _converter = new showdown.Converter({
  extensions: [showdownHighlight({ pre: true })],
  simpleLineBreaks: true,
  disableForced4SpacesIndentedSublists: true,
  openLinksInNewWindow: true,
  literalMidWordUnderscores: true
});

const _delimiters = [
  {left: "$$", right: "$$", display: true},
  {left: "$", right: "$", display: false},
  {left: "\\(", right: "\\)", display: false},
  {left: "\\begin{equation}", right: "\\end{equation}", display: true},
  {left: "\\begin{align}", right: "\\end{align}", display: true},
  {left: "\\begin{alignat}", right: "\\end{alignat}", display: true},
  {left: "\\begin{gather}", right: "\\end{gather}", display: true},
  {left: "\\begin{CD}", right: "\\end{CD}", display: true},
  {left: "\\[", right: "\\]", display: true},
];

function processQuizText(text) {
  if (!text) return '';
  try {
    let split = splitAtDelimiters(text, [..._delimiters, {left: '`', right: '`', display: true}]);
    let hybridText = "";
    let mathBlocks = [];
    for (let i = 0; i < split.length; i++) {
      let chunk = split[i];
      if (chunk.type === 'text') {
        hybridText += chunk.data;
      } else if (chunk.type === 'math') {
        if (chunk.rawData.startsWith("`") && chunk.rawData.endsWith("`")) {
          hybridText += chunk.rawData;
        } else {
          mathBlocks[i] = katex.renderToString(chunk.data, {
            delimiters: _delimiters,
            displayMode: chunk.display,
            throwOnError: false
          });
          hybridText += '`__KATEX__' + i + '__`';
        }
      }
    }
    hybridText = _converter.makeHtml(hybridText);
    for (let i = 0; i < mathBlocks.length; i++) {
      hybridText = hybridText.replace('<code>__KATEX__' + i + '__</code>', mathBlocks[i]);
    }
    return hybridText ? `<span class="markdown">${hybridText}</span>` : text;
  } catch (e) {
    console.log("Error processing quiz text:", e);
    return text;
  }
}

export default {
  name:'InteractiveQuiz',
  setup() {
    const theme = useTheme();
    const isDarkTheme = computed(() => theme.global.current.value.dark);
    return { isDarkTheme }
  },

  computed: {
    totalQuestions() {
      return this.quizData.length;
    },
    allQuestionsAnswered() {
      return this.quizData.length > 0 &&
        this.answers.length === this.quizData.length &&
        this.answers.every(answer => answer !== null && answer !== undefined);

    },
    buttonColor() {
      if (this.isSubmitted) {
        return '#e21833';
      } else if (this.allQuestionsAnswered) {
        return '#e21833';
      } else {
        return '#7f7f7f';
      }
    }
  },
  methods: {
    formatText(text) {
      return processQuizText(text);
    },
    submitQuiz() {
      this.isSubmitted = true;
      this.calculateScore();
    },
    confirmReset() {
      this.showResetConfirmation = true;
    },

    confirmAndReset() {
      this.resetQuiz();
      this.showResetConfirmation = false;
    },

    resetQuiz() {
      this.answers = Array(this.quizData.length).fill(null);
      this.isSubmitted = false;
      this.totalCorrect = 0;
      // You might want to scroll to the top of the quiz here
      // window.scrollTo(0, 0);
    },


    calculateScore() {
      this.totalCorrect = this.answers.reduce((correct, answer, index) => {
        return correct + (answer === this.quizData[index]['correctAnswerLetter'] ? 1 : 0);
      }, 0);
    },
    isCorrect(questionIndex,answerKey){
      // return this.answers[questionIndex] === answerIndex && this.answers[questionIndex] === this.quizData[questionIndex]['solution-index'];
    //  return this.answers[questionIndex]!=null && this.quizData[questionIndex]['correctAnswerLetter']===answerKey
      return this.isSubmitted &&  this.quizData[questionIndex]['correctAnswerLetter'] === answerKey;
    },
    isIncorrect(questionIndex,answerKey){
      // return this.answers[questionIndex] === answerKey && this.answers[questionIndex] !== this.quizData[questionIndex]['correctAnswerLetter'];
      return this.isSubmitted && this.answers[questionIndex] === answerKey && this.quizData[questionIndex]['correctAnswerLetter'] !== answerKey;
    },
    prepareQuizSourcesForDisplay(message) {
      console.log('message: ',message)
      if (typeof message.source === 'object' && message.source !== null) {
        console.log('message source: ',message.source)
        let sourceEntries = Object.entries(message.source); // Convert object to array of entries
        console.log('sourceEntries: ',sourceEntries)
        return sourceEntries.map((entry) => {
          console.log('entry: ',entry)
          let source = entry[1];
          console.log('source: ',source)
          const videoTimestampRegex = /\d{2}:\d{2}:\d{2}/;
          let isVideo = videoTimestampRegex.test(source);
          let isLink = false;
          if (!isVideo) {
            const docLinkRegex = /<a\s+(?:.*?\s+)?href=["'](.*?)["'](.*?)>(.*?)\.(pdf|doc|docx|gdoc|gsheet|ppt|pptx)\b/i;
            isLink = docLinkRegex.test(source); // Check for links if it's not a video
          }
          return {
            source,
            isVideo,
            isLink
          };
        });
      } else {
        console.error('Expected an object for sources, received:', message.sources);
        return []; // Return an empty array
      }
    }
  },
  props:['quizData'],
  data() {
    return {
      answers: Array(this.quizData.length).fill(null),
      isSubmitted:false,
      showResetConfirmation: false,
      totalCorrect: 0,

    };
  },
}
</script>
<style>
.tooltip {
  position: absolute;
  top: -10px;
  border: 1px solid #ccc; /* Optional border */
  font-family: Roboto, -apple-system, sans-serif;
  text-transform: capitalize;
  font-size: 11px;
  opacity: 0.75;
  background-color: #323232;
  color: #fff;
  padding: 4px;
  border-radius: 3px;
  display: block;
}
::placeholder {
  color: #888888; /* Gray text */
  font-style: italic;
}

.quiz-container .v-card-title {
  white-space: unset;
}
.quiz-container div .v-selection-control--disabled {
  opacity: .65;
}
.quiz-answer > div {
  display: inline;
}
.quiz-answer {
  padding-left: 2px;
  padding-right: 2px;
}
.quiz-justification {
  margin-left: 40px;
}
.quiz-justification ol {
  font-size: 10.5pt;
  font-weight: bolder;
}
.quiz-justification li {
  font-weight: normal;
}
.botText .quiz-container span:not(.katex *) {
  line-height: unset;
  display: unset;
  background: unset;
  padding: unset;
  border-radius: unset;
  max-width: unset;
  margin-left: unset;
  animation: unset;
}
.quiz-container ul {
  padding-left: 40px

}
.quiz-container li {
  font-size: 10pt
}
.quiz-answer.incorrect {
  background-color: red;
}
.quiz-answer.correct {
  background-color: green;
}
.dark-theme .quiz-answer.incorrect {
  background-color: #8B0000; /* Darker red for dark mode */
  color: #E0E0E0; /* Off-white text */
}

.dark-theme .quiz-answer.correct {
  background-color: #006400; /* Darker green for dark mode */
  color: #E0E0E0; /* Off-white text */
}
.v-input--disabled .quiz-justification {
  pointer-events: all;
}
#quizSourcesContainer .video-icon {
  font-size: 12px;
  padding-left: 7px;
  padding-bottom: 1px;
  color: #636363;
}

#quizSourcesContainer .doc-icon {
  font-size: 12px;
  padding-left: 7px;
  padding-bottom: 1px;
  color: #636363;
}

.disclaimer {
  color: #e21833;
}
.dark-theme .v-card-title {
  color: #E0E0E0;
}

.dark-theme .quiz-answer {
  color: #E0E0E0;
}

.dark-theme .quiz-justification {
  color: #E0E0E0;
}

.dark-theme .mb-0 {
  color: #E0E0E0;
}

.dark-theme #quizSourcesContainer {
  color: #E0E0E0;
}

.dark-theme .disclaimer {
  color: #FF6B6B; /* Lighter red for dark mode */
}

/* Custom tooltip styles */
.button-tooltip-container {
  position: relative;
}

.custom-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #323232;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
  z-index: 1000;
  margin-bottom: 5px;
}

.custom-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #323232;
}

.button-tooltip-container:hover .custom-tooltip {
  opacity: 1;
  visibility: visible;
}
</style>
